import React from 'react';
import { ExternalLink } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardBody, Button, Input, Textarea } from '../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { SessionImageStrip, SessionSponsorLogoSummary } from '../components/SessionSponsorContent';
import { conferenceHeaderLabel, isLogisticsSession, sessionHasDetail } from '../lib/conference';
import {
  DEFAULT_CONFERENCE_ID,
  getConference,
  subscribeConferenceSessions,
  createSession,
  listSessionAttendancePlans,
  setSessionAttendancePlan,
} from '../lib/firestore';
import type { Conference, Session } from '../lib/types';

const SHOW_CREATE_SESSION_BUTTON = false;
const FALLBACK_CONFERENCE: Conference = {
  id: DEFAULT_CONFERENCE_ID,
  name: '2026 Conference',
  year: 2026,
  startAt: Date.UTC(2026, 5, 10, 12, 0, 0, 0),
  endAt: Date.UTC(2026, 5, 10, 16, 30, 0, 0),
};
const SESSION_CACHE_PREFIX = 'conference.sessions.';

function sessionCacheKey(conferenceId: string) {
  return `${SESSION_CACHE_PREFIX}${conferenceId}`;
}

function readCachedSessions(conferenceId: string): Session[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(sessionCacheKey(conferenceId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCachedSessions(conferenceId: string, sessions: Session[]) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(sessionCacheKey(conferenceId), JSON.stringify(sessions));
  } catch {
    // Best effort only; the live Firestore subscription remains authoritative.
  }
}

function parseLocalIsoLikeToMs(s: string): number | null {
  // Parses strings like:
  //  - YYYY-MM-DD
  //  - YYYY-MM-DDTHH:mm
  //  - YYYY-MM-DDTHH:mm:ss
  //  - YYYY-MM-DDTHH:mm:ss.SSS
  // WITHOUT timezone suffix. Interprets as local time.
  const m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?$/
  );
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const hour = m[4] ? Number(m[4]) : 0;
  const minute = m[5] ? Number(m[5]) : 0;
  const second = m[6] ? Number(m[6]) : 0;
  const ms = m[7] ? Number(String(m[7]).padEnd(3, '0')) : 0;
  const d = new Date(year, month - 1, day, hour, minute, second, ms);
  const out = d.getTime();
  return Number.isFinite(out) ? out : null;
}

function coerceToMs(v: any): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;

  if (typeof v === 'string') {
    const hasTz = /([zZ]|[+-]\d{2}:?\d{2})$/.test(v);
    if (!hasTz) {
      const local = parseLocalIsoLikeToMs(v);
      if (local != null) return local;
    }
    const parsed = Date.parse(v);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (v && typeof v === 'object') {
    if (typeof (v as any).toMillis === 'function') {
      const ms = (v as any).toMillis();
      return typeof ms === 'number' && Number.isFinite(ms) ? ms : null;
    }
    if (typeof (v as any).seconds === 'number') {
      const sec = (v as any).seconds;
      const ns = typeof (v as any).nanoseconds === 'number' ? (v as any).nanoseconds : 0;
      const ms = sec * 1000 + Math.floor(ns / 1_000_000);
      return Number.isFinite(ms) ? ms : null;
    }
  }

  return null;
}

function fmtDateTime(v: any) {
  const ms = coerceToMs(v);
  return ms != null ? new Date(ms).toLocaleString() : '--';
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error('Timed out loading conference')), timeoutMs);
    promise
      .then((value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      });
  });
}

function fmtDate(v: any) {
  const ms = coerceToMs(v);
  return ms != null ? new Date(ms).toLocaleDateString() : '--';
}

function fmtTime(v: any) {
  const ms = coerceToMs(v);
  return ms != null ? new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--';
}

function fmtTimeRange(startAt: any, endAt: any) {
  return `${fmtTime(startAt)} - ${fmtTime(endAt)}`;
}

function sameLocalDate(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function fmtDateTimeRange(startAt: any, endAt: any) {
  const sMs = coerceToMs(startAt);
  const eMs = coerceToMs(endAt);
  if (sMs == null || eMs == null) return `${fmtDateTime(startAt)} - ${fmtDateTime(endAt)}`;

  const s = new Date(sMs);
  const e = new Date(eMs);
  const sDate = s.toLocaleDateString();
  const sTime = s.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const eDate = e.toLocaleDateString();
  const eTime = e.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (sameLocalDate(s, e)) {
    return `${sDate}, ${sTime} - ${eTime}`;
  }
  return `${sDate}, ${sTime} - ${eDate}, ${eTime}`;
}

const TIME_OPTIONS: string[] = (() => {
  const opts: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      opts.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return opts;
})();

function addMinutesToTime(timeHHMM: string, minutesToAdd: number) {
  const [hh, mm] = timeHHMM.split(':').map((x) => parseInt(x, 10));
  const base = hh * 60 + mm;
  const total = (base + minutesToAdd) % (24 * 60);
  const outH = Math.floor(total / 60);
  const outM = total % 60;
  return `${String(outH).padStart(2, '0')}:${String(outM).padStart(2, '0')}`;
}

function msFromDateAndTime(dateYYYYMMDD: string, timeHHMM: string): number {
  const [y, m, d] = dateYYYYMMDD.split('-').map((x) => parseInt(x, 10));
  const [hh, mm] = timeHHMM.split(':').map((x) => parseInt(x, 10));
  return new Date(y, m - 1, d, hh, mm, 0, 0).getTime();
}

function toDateLocal(ms: number) {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function streamOrder(session: Session) {
  const room = String(session.room ?? '');
  const normalizedRoom = room.trim().toLowerCase();
  if (normalizedRoom === 'music room') return 1;
  if (normalizedRoom === 'williams suite 1') return 2;
  if (normalizedRoom === 'williams suite 2') return 3;

  const match = room.match(/^Stream\s+(\d+)/i);
  return match ? Number(match[1]) : 99;
}

function sessionPlanningSlotKey(session: Session) {
  const startMs = coerceToMs((session as any).startAt);
  if (startMs == null) return null;
  const endMs = coerceToMs((session as any).endAt);
  return `${startMs}:${endMs ?? ''}`;
}

function planningClasses(active: boolean, disabled: boolean) {
  return [
    'inline-flex min-h-10 w-full items-center justify-center rounded-xl border px-3 py-2 text-sm font-semibold transition',
    active
      ? 'border-red-600 bg-red-600 text-white'
      : 'border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-50',
    disabled ? 'cursor-wait opacity-70' : 'cursor-pointer',
  ].join(' ');
}

function DiscussionOpenGlyph() {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute right-2.5 top-2.5 inline-flex h-7 w-7 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-600 shadow-sm"
    >
      <ExternalLink size={15} strokeWidth={2.2} />
    </span>
  );
}

function showAgendaSessionImages(session: Session) {
  return session.id !== 's24-agm-prize-draw';
}

export default function ConferencePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightedSessionId = searchParams.get('session') ?? '';
  const highlightedSessionRef = React.useRef<HTMLDivElement | null>(null);
  const [conferences, setConferences] = React.useState<Conference[]>([FALLBACK_CONFERENCE]);
  const [selectedId, setSelectedId] = React.useState<string | null>(DEFAULT_CONFERENCE_ID);
  const [sessions, setSessions] = React.useState<Session[]>(() => readCachedSessions(DEFAULT_CONFERENCE_ID));
  const [conferenceLoading, setConferenceLoading] = React.useState(true);
  const [conferenceError, setConferenceError] = React.useState<string | null>(null);
  const [sessionsLoading, setSessionsLoading] = React.useState(false);
  const [sessionsError, setSessionsError] = React.useState<string | null>(null);
  const [plannedById, setPlannedById] = React.useState<Record<string, boolean>>({});
  const [savingPlanById, setSavingPlanById] = React.useState<Record<string, boolean>>({});
  const [planError, setPlanError] = React.useState<string | null>(null);

  // Create session form
  const [openCreate, setOpenCreate] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [abstract, setAbstract] = React.useState('');
  const [room, setRoom] = React.useState('');
  const [presenter, setPresenter] = React.useState('');
  const [hideInterest, setHideInterest] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  // Default: ~30 days out at 00:00. End defaults to +60 mins, same date.
  const defaultStartMs = React.useMemo(() => {
    const d = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, []);

  const [startDate, setStartDate] = React.useState(toDateLocal(defaultStartMs));
  const [startTime, setStartTime] = React.useState('00:00');
  const [endDate, setEndDate] = React.useState(toDateLocal(defaultStartMs));
  const [endTime, setEndTime] = React.useState('01:00');
  const conferenceLoadSeq = React.useRef(0);
  const conferenceStateRef = React.useRef({
    loading: conferenceLoading,
    selectedId,
    error: conferenceError,
  });
  const sessionsRef = React.useRef(sessions);

  function syncEndFromStart(nextStartDate: string, nextStartTime: string) {
    setEndDate(nextStartDate);
    setEndTime(addMinutesToTime(nextStartTime, 60));
  }

  function onStartDateChange(v: string) {
    setStartDate(v);
    syncEndFromStart(v, startTime);
  }

  function onStartTimeChange(v: string) {
    setStartTime(v);
    syncEndFromStart(startDate, v);
  }

  React.useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  React.useEffect(() => {
    conferenceStateRef.current = {
      loading: conferenceLoading,
      selectedId,
      error: conferenceError,
    };
  }, [conferenceLoading, selectedId, conferenceError]);

  const loadConference = React.useCallback(() => {
    const seq = conferenceLoadSeq.current + 1;
    conferenceLoadSeq.current = seq;
    setConferenceLoading(true);
    setConferenceError(null);

    withTimeout(getConference(DEFAULT_CONFERENCE_ID), 10000)
      .then((item) => {
        if (conferenceLoadSeq.current !== seq) return;
        setConferences([item ?? FALLBACK_CONFERENCE]);
        setSelectedId(DEFAULT_CONFERENCE_ID);
      })
      .catch((error) => {
        if (conferenceLoadSeq.current !== seq) return;
        console.warn('Could not refresh conference metadata; using configured conference fallback.', error);
        setConferences((items) => (items.length ? items : [FALLBACK_CONFERENCE]));
        setSelectedId((id) => id ?? DEFAULT_CONFERENCE_ID);
        setConferenceError(null);
      })
      .finally(() => {
        if (conferenceLoadSeq.current === seq) setConferenceLoading(false);
      });
  }, []);

  React.useEffect(() => {
    loadConference();
    return () => {
      conferenceLoadSeq.current += 1;
    };
  }, [loadConference]);

  React.useEffect(() => {
    const reloadIfNeeded = (force = false) => {
      const state = conferenceStateRef.current;
      if (force || state.error || !state.selectedId) loadConference();
    };
    const onFocus = () => reloadIfNeeded();
    const onOnline = () => reloadIfNeeded();
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) reloadIfNeeded(true);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') reloadIfNeeded();
    };

    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [loadConference]);

  React.useEffect(() => {
    setSessionsError(null);

    if (!selectedId) {
      setSessions([]);
      setSessionsLoading(false);
      return;
    }

    const cachedSessions = readCachedSessions(selectedId);
    if (cachedSessions.length) {
      setSessions(cachedSessions);
      setSessionsLoading(false);
    } else {
      setSessionsLoading(sessionsRef.current.length === 0);
    }

    const unsub = subscribeConferenceSessions(
      selectedId,
      (items) => {
        setSessions(items);
        writeCachedSessions(selectedId, items);
        setSessionsError(null);
        setSessionsLoading(false);
      },
      () => {
        const fallbackSessions = readCachedSessions(selectedId);
        if (fallbackSessions.length) {
          setSessions(fallbackSessions);
          setSessionsError(null);
        } else {
          setSessionsError('Could not load conference sessions. Please try again.');
        }
        setSessionsLoading(false);
      }
    );
    return () => unsub();
  }, [selectedId]);

  const conf = conferences.find((c) => c.id === selectedId) ?? null;

  React.useEffect(() => {
    if (!highlightedSessionId || sessions.length === 0) return;
    window.setTimeout(() => {
      highlightedSessionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }, [highlightedSessionId, sessions.length]);

  React.useEffect(() => {
    if (!selectedId || !user?.sub || sessions.length === 0) {
      setPlannedById({});
      return;
    }

    let cancelled = false;
    const sessionIds = sessions.map((session) => session.id);

    listSessionAttendancePlans(selectedId, sessionIds, user.sub)
      .then((items) => {
        if (cancelled) return;
        setPlannedById(
          Object.fromEntries(
            items
              .filter((item) => item.status === 'planning')
              .map((item) => [item.sessionId, true]),
          ),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setPlannedById({});
          setPlanError('Could not load your session choices.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedId, sessions, user?.sub]);

  const groupedSessions = React.useMemo(() => {
    const withStart = sessions
      .map((s) => ({ s, startMs: coerceToMs((s as any).startAt) ?? 0 }))
      .sort((a, b) => a.startMs - b.startMs);

    const groups: Array<{ key: string; startMs: number; items: Session[] }> = [];
    for (const row of withStart) {
      const key = String(row.startMs);
      const last = groups[groups.length - 1];
      if (!last || last.key !== key) {
        groups.push({ key, startMs: row.startMs, items: [row.s] });
      } else {
        last.items.push(row.s);
      }
    }
    for (const group of groups) {
      group.items.sort((a, b) => streamOrder(a) - streamOrder(b) || a.title.localeCompare(b.title));
    }
    return groups;
  }, [sessions]);

  async function togglePlanning(session: Session, planning: boolean) {
    const targetSlotKey = sessionPlanningSlotKey(session);
    const conflictingPlannedIds = planning
      ? sessions
          .filter((candidate) => (
            targetSlotKey != null &&
            candidate.id !== session.id &&
            Boolean(plannedById[candidate.id]) &&
            sessionPlanningSlotKey(candidate) === targetSlotKey
          ))
          .map((candidate) => candidate.id)
      : [];
    const affectedIds = [session.id, ...conflictingPlannedIds];

    if (!conf || !user?.sub || affectedIds.some((id) => savingPlanById[id])) return;

    const previousById = Object.fromEntries(affectedIds.map((id) => [id, Boolean(plannedById[id])]));
    setPlanError(null);
    setPlannedById((prev) => {
      const next = { ...prev, [session.id]: planning };
      if (planning) {
        conflictingPlannedIds.forEach((id) => {
          next[id] = false;
        });
      }
      return next;
    });
    setSavingPlanById((prev) => {
      const next = { ...prev };
      affectedIds.forEach((id) => {
        next[id] = true;
      });
      return next;
    });

    try {
      await setSessionAttendancePlan({
        conferenceId: conf.id,
        sessionId: session.id,
        profileId: user.sub,
        planning,
      });
    } catch (e: any) {
      setPlannedById((prev) => ({ ...prev, ...previousById }));
      setPlanError(e?.message || 'Could not save your session choice.');
    } finally {
      setSavingPlanById((prev) => {
        const next = { ...prev };
        affectedIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    }
  }

  function openSessionDiscussion(session: Session) {
    if (!conf || !sessionHasDetail(session)) return;
    nav(`/conference/${encodeURIComponent(session.id)}`);
  }

  function onSessionCardKeyDown(event: React.KeyboardEvent<HTMLDivElement>, session: Session) {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    openSessionDiscussion(session);
  }

  async function submitSession() {
    if (!conf) return;

    setCreateError(null);

    const startMs = msFromDateAndTime(startDate, startTime);
    let endMs = msFromDateAndTime(endDate, endTime);

    if (!title.trim() || !abstract.trim() || !Number.isFinite(startMs) || !Number.isFinite(endMs)) {
      setCreateError('Please complete title, abstract and valid start/end times.');
      return;
    }

    // Guard: if end <= start, default to +60 mins
    if (endMs <= startMs) {
      endMs = startMs + 60 * 60 * 1000;
      setEndDate(startDate);
      setEndTime(addMinutesToTime(startTime, 60));
    }

    const payload: any = {
      title: title.trim(),
      abstract: abstract.trim(),
      startAt: startMs,
      endAt: endMs,
      ...(hideInterest ? { hideInterest: true } : {})
    };

    // Firestore does not allow `undefined` values. Only include optional fields when provided.
    const roomValue = room.trim();
    if (roomValue) payload.room = roomValue;

    const speakerValue = presenter.trim();
    payload.presenters = speakerValue ? [{ name: speakerValue }] : [];

    try {
      const fn: any = createSession as any;

      // Support either createSession(conferenceId, data) or createSession(dataWithConferenceId)
      if (typeof fn === 'function' && fn.length >= 2) {
        await fn(conf.id, payload);
      } else {
        await fn({ conferenceId: conf.id, ...payload });
      }

      setTitle('');
      setAbstract('');
      setRoom('');
      setPresenter('');
      setHideInterest(false);
      setOpenCreate(false);
    } catch (e: any) {
      console.error(e);
      setCreateError(e?.message || 'Failed to create session');
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      <Card className="overflow-hidden">
        {/* Header styled to match Home (zinc strip) */}
        <div className="flex items-center justify-between gap-3 border-b border-zinc-300 bg-zinc-200 px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-zinc-900">
              {conferenceLoading && !conf
                ? 'Conference'
                : conf
                  ? conferenceHeaderLabel(coerceToMs(conf.startAt))
                  : 'No conference configured'}
            </div>
          </div>
          {SHOW_CREATE_SESSION_BUTTON ? (
            <div className="flex shrink-0 items-center gap-2">
              <Button onClick={() => setOpenCreate((v) => !v)}>New session</Button>
            </div>
          ) : null}
        </div>

        <CardBody className="space-y-3">
          {conferenceLoading && !conf ? (
            <div className="text-sm text-zinc-600">Loading conference...</div>
          ) : conferenceError && !conf ? (
            <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {conferenceError}
            </div>
          ) : !conf ? (
            <div className="text-sm text-zinc-600">Seed a conference in Firestore to activate this area.</div>
          ) : null}

          {openCreate && conf ? (
            <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3">
              <div className="grid grid-cols-1 gap-2">
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Session title" />
                <Textarea value={abstract} onChange={(e) => setAbstract(e.target.value)} placeholder="Abstract" rows={4} />

                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <Input value={presenter} onChange={(e) => setPresenter(e.target.value)} placeholder="Presenter name" />
                  <Input value={room} onChange={(e) => setRoom(e.target.value)} placeholder="Room (optional)" />
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <div>
                    <div className="mb-1 text-xs font-semibold">Start</div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <Input type="date" value={startDate} onChange={(e) => onStartDateChange(e.target.value)} />
                      <select
                        className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm"
                        value={startTime}
                        onChange={(e) => onStartTimeChange(e.target.value)}
                      >
                        {TIME_OPTIONS.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <div className="mb-1 text-xs font-semibold">End</div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                      <select
                        className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                      >
                        {TIME_OPTIONS.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <label className="mt-1 flex items-center gap-2 text-sm text-zinc-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={hideInterest}
                    onChange={(e) => setHideInterest(e.target.checked)}
                  />
                  Hide interest
                </label>

                {createError ? (
                  <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {createError}
                  </div>
                ) : null}

                <div className="flex items-center justify-between">
                  <div className="text-xs text-zinc-600">
                    {fmtDateTimeRange(
                      msFromDateAndTime(startDate, startTime),
                      msFromDateAndTime(endDate, endTime)
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={() => setOpenCreate(false)}>
                      Cancel
                    </Button>
                    <Button onClick={submitSession} disabled={!title.trim() || !abstract.trim()}>
                      Create
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {planError ? (
            <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {planError}
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-2">
            {conf && sessionsLoading && sessions.length === 0 ? (
              <div className="text-sm text-zinc-600">Loading sessions...</div>
            ) : null}

            {conf && sessionsError ? (
              <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {sessionsError}
              </div>
            ) : null}

            {conf && !sessionsLoading && !sessionsError && sessions.length === 0 ? (
              <div className="text-sm text-zinc-600">No sessions</div>
            ) : null}

            {groupedSessions.map((g, idx) => (
              <section
                key={g.key}
                className={[
                  'grid grid-cols-1 gap-2',
                  idx === 0 ? '' : 'mt-2 border-t border-zinc-100 pt-3',
                  g.items.length > 1 ? 'rounded-xl border border-zinc-200/80 bg-zinc-50 p-2' : '',
                ].join(' ')}
              >
                {g.items.length > 1 ? <div className="px-1 text-xs font-medium text-zinc-500">Choice of:</div> : null}
                <div className={`grid grid-cols-1 gap-2 ${g.items.length > 1 ? 'lg:grid-cols-3' : ''}`}>
                  {g.items.map((s) => {
                    const hide = Boolean(s.hideInterest);
                    const hasDetail = sessionHasDetail(s);
                    const isLogistics = isLogisticsSession(s);
                    const planned = Boolean(plannedById[s.id]);
                    const saving = Boolean(savingPlanById[s.id]);
                    const agendaAbstract = s.agendaAbstract ?? s.abstract;
                    const isHighlighted = highlightedSessionId === s.id;
                    const presenterNames = (s.presenters ?? [])
                      .map((p) => (p?.name ?? '').trim())
                      .filter((n) => n && n.toUpperCase() !== 'TBC');
                    return (
                      <div
                        key={s.id}
                        ref={isHighlighted ? highlightedSessionRef : undefined}
                        role={hasDetail ? 'link' : undefined}
                        tabIndex={hasDetail ? 0 : undefined}
                        onClick={() => openSessionDiscussion(s)}
                        onKeyDown={(event) => onSessionCardKeyDown(event, s)}
                        className={[
                          'relative rounded-2xl border border-zinc-200 p-3',
                          isLogistics ? 'bg-[#eaf4ff]' : 'bg-white',
                          hasDetail ? 'cursor-pointer transition hover:border-zinc-300 hover:bg-zinc-50' : '',
                          isHighlighted ? 'ring-2 ring-red-500 ring-offset-2' : '',
                        ].join(' ')}
                      >
                        {hasDetail ? <DiscussionOpenGlyph /> : null}
                        <div className="flex h-full flex-col gap-3">
                          <div className="min-w-0 flex-1">
                            <div className={hasDetail ? 'pr-8' : ''}>
                              <div className="text-sm font-semibold">{s.title}</div>
                              {agendaAbstract ? (
                                <div className="mt-1 whitespace-pre-wrap text-sm text-zinc-700">{agendaAbstract}</div>
                              ) : null}
                            </div>
                            {showAgendaSessionImages(s) ? <SessionImageStrip images={s.sessionImages} /> : null}
                            <SessionSponsorLogoSummary groups={s.sponsorGroups} />
                            <div className="mt-2 text-xs text-zinc-600">
                              {fmtTimeRange((s as any).startAt, (s as any).endAt)}
                              {s.room ? ` | ${s.room}` : ''}
                              {presenterNames.length ? ` | ${presenterNames.join(', ')}` : ''}
                            </div>
                          </div>

                          {!hide ? (
                            <button
                              type="button"
                              className={planningClasses(planned, saving || !user?.sub)}
                              disabled={saving || !user?.sub}
                              onClick={(event) => {
                                event.stopPropagation();
                                togglePlanning(s, !planned);
                              }}
                            >
                              {saving ? 'Saving...' : 'Planning to attend'}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
